import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAPIKeyCountQuery,
  useAPIKeyListQuery,
  useAPIKeyRemoveMutation,
  useAPIKeyUpdateMutation,
} from '@/hook/apiKey';
import {
  KeyVertical,
  VFlex,
  VLoadingSpinner,
  VStack,
  VStatusBadge,
  VSwitch,
  VTable,
  formatDate,
  useVModal,
} from '@/package';
import { useAuthStore } from '@/store';
import type { APIKeyWithPermission } from '@/type';

import { APIKeyCreationButton } from './APIKeyCreationButton';
import { APIKeyDetailModal } from './APIKeyDetailModal';

export const APIKeyList = () => {
  const { t } = useTranslation();
  const user = useAuthStore(state => state.user);
  const { data: apiKeyList, isLoading } = useAPIKeyListQuery();
  const { data: apiKeyCount } = useAPIKeyCountQuery({});
  const { data: activatedCount } = useAPIKeyCountQuery({ status: 'Enable' });
  const { mutateAsync: update } = useAPIKeyUpdateMutation();
  const { mutateAsync: remove } = useAPIKeyRemoveMutation();
  const { openDangerConfirm } = useVModal();
  const [selectedAPIKey, setSelectedAPIKey] =
    useState<Nullish<APIKeyWithPermission>>(null);
  const apiKeyTableData = useMemo(() => {
    if (isLoading || apiKeyList == null) return [];

    return apiKeyList.map(item => {
      const isEditablePermission =
        useAuthStore.getState().isAdmin() ||
        (useAuthStore.getState().isManager() && user.id === item.user.id);

      return {
        id: item.id,
        keyName: item.name,
        status: item.status,
        usage: item.usage,
        secretKey: '************',
        lastAccessed: item.lastUseTimeAt ? formatDate(item.lastUseTimeAt) : '-',
        createdBy: item.user.email,
        expirationTime: formatDate(item.expireAt),
        activate:
          item.status === 'Enable'
            ? true
            : item.status === 'Disable'
              ? false
              : null,
        raw: {
          ...item,
          isEditablePermission,
        },
      };
    });
  }, [apiKeyList, isLoading, user]);

  return (
    <VStack className="h-full py-4" gap={0.25}>
      {isLoading ? (
        <VLoadingSpinner />
      ) : (
        <>
          <VFlex
            className="px-2 text-body-xl text-gray-500"
            gap={0.5}
            items="center"
          >
            <div>
              <span>Total&nbsp;</span>
              <span>{apiKeyCount ?? 0}</span>
            </div>
            {activatedCount && (
              <>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="font-semibold text-gray-500">
                  {t('apiKey.activateCount.seborrhea', {
                    count: activatedCount,
                  })}
                </div>
              </>
            )}
          </VFlex>

          <VTable
            className="h-full overflow-hidden"
            columnList={[
              'keyName',
              'status',
              'usage',
              'secretKey',
              'lastAccessed',
              'createdBy',
              'expirationTime',
              'activate',
            ]}
            columnStyleMap={{
              status: { className: 'w-[86px]' },
              usage: { className: 'w-[148px]' },
              secretKey: { className: 'w-[164px]' },
              lastAccessed: { className: 'w-[204px]' },
              createdBy: { className: 'w-[124px]' },
              expirationTime: { className: 'w-[204px]' },
              activate: { className: 'w-[68px]' },
            }}
            cellRenderer={{
              status: value => (
                <VStatusBadge
                  size="sm"
                  variant={
                    value === 'Disable'
                      ? 'notStarted'
                      : value === 'Enable'
                        ? 'inProgress'
                        : 'ready'
                  }
                >
                  {value}
                </VStatusBadge>
              ),
              usage: value => <div className="text-right">{value}</div>,
              activate: (value, _, { id, keyName, raw }) =>
                value != null ? (
                  <VFlex items="center" onClick={e => e.stopPropagation()}>
                    <VSwitch
                      checked={value}
                      disabled={!raw.isEditablePermission}
                      onChange={value => {
                        update({
                          id,
                          name: keyName,
                          status: value === false ? 'Disable' : 'Enable',
                        });
                      }}
                    />
                  </VFlex>
                ) : (
                  ''
                ),
            }}
            data={apiKeyTableData}
            renderEmptyData={() => (
              <VStack className="w-full pt-40" gap={0.5} items="center">
                <VFlex>
                  <KeyVertical className="size-16.5 text-blue-300" />
                </VFlex>

                <VStack gap={1} items="center">
                  <div className="text-center leading-[24px] whitespace-pre-line text-blue-300">
                    {t('apiKey.listEmptyMessage.curvilinear')}
                  </div>

                  <APIKeyCreationButton />
                </VStack>
              </VStack>
            )}
            resizable
            setRowRemovable={row => row.status != null}
            setRowSelected={row => row.status !== 'Inactive'}
            onRowClick={e => {
              const apiKey = e.target.row.raw as APIKeyWithPermission;

              setSelectedAPIKey(apiKey);
            }}
            onRemove={id => {
              openDangerConfirm({
                message: t('project.removeWarnTitle.falconidae'),
                title: t('project.removeAction.kalashnikov'),
                size: 'small',
                onConfirm() {
                  remove(id);
                },
              });
            }}
          />

          {selectedAPIKey && (
            <APIKeyDetailModal
              {...selectedAPIKey}
              onHide={() => setSelectedAPIKey(null)}
            />
          )}
        </>
      )}
    </VStack>
  );
};

APIKeyList.displayName = 'APIKeyList';
